import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addProjectContextEntry,
  getProjectContext,
  saveProjectContextDocument,
  type ProjectContextPayload,
} from '../lib/projectContextApi';

export const projectContextKey = (projectId: string) => ['project-context', projectId] as const;

export function useProjectContext(projectId: string | undefined) {
  return useQuery({
    queryKey: projectContextKey(projectId ?? ''),
    queryFn: () => getProjectContext(projectId as string),
    enabled: Boolean(projectId),
  });
}

/** Rewrite the consolidated document by hand. */
export function useSaveProjectContextDocument(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (markdown: string) => saveProjectContextDocument(projectId, markdown),
    onSuccess: (document) => {
      qc.setQueryData<ProjectContextPayload>(projectContextKey(projectId), (prev) =>
        prev ? { ...prev, document } : prev,
      );
    },
  });
}

/** Add a direct note (no chat in between); the document is consolidated server-side. */
export function useAddProjectContextEntry(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) => addProjectContextEntry(projectId, content),
    onSuccess: (result) => {
      qc.setQueryData<ProjectContextPayload>(projectContextKey(projectId), (prev) =>
        prev
          ? { document: result.document, entries: [result.entry, ...prev.entries] }
          : { document: result.document, entries: [result.entry] },
      );
    },
  });
}
